import { createApi } from '@reduxjs/toolkit/query/react';
import {
  FilterDTOType,
  WorkflowCreateDTOType,
  WorkflowDTOType,
  WorkflowItemDTOType,
} from '../../schemas';
import { overrideFetchBaseQuery } from '../utils/overrideFetchBaseQuery';
import { baseQueryWithZodValidation } from '../utils/baseQueryWithZodValidation';

async function updateWorkflowInInfiniteQuery(id: number, dispatch: any) {
  const updated = await dispatch(
    workflowApi.endpoints.getWorkflowById.initiate(
      { id },
      {
        forceRefetch: true,
      },
    ),
  ).unwrap();

  dispatch(
    workflowApi.util.updateQueryData('searchWorkflows', { filters: [] }, draft => {
      draft.pages.forEach(page => {
        const idx = page.findIndex(wf => wf.id === id);
        if (idx !== -1) {
          page[idx] = updated;
        }
      });
    }),
  );
}

export const workflowApi = createApi({
  reducerPath: 'workflowApi',
  baseQuery: baseQueryWithZodValidation(overrideFetchBaseQuery({}), {}),
  tagTypes: [
    'Workflow',
    'QCPendingItems',
    'QCPassedItems',
    'QCFailedItems',
    'QCProcessingItems',
    'QCProcessingItems',
    'QCPendingItemsByTag',
  ],
  endpoints: builder => ({
    // * ################################################################
    createWorkflow: builder.mutation<WorkflowDTOType, WorkflowCreateDTOType>({
      query: workflowData => ({
        url: '/catalog/workflow/',
        method: 'POST',
        body: workflowData,
      }),
      invalidatesTags: ['Workflow'],
    }),
    // * ################################################################
    updateWorkflow: builder.mutation<WorkflowDTOType, { id: number } & Partial<WorkflowDTOType>>({
      query: ({ id, ...body }) => ({
        url: `/catalog/workflow/${id}`,
        method: 'PUT',
        body,
      }),
    }),
    // * ################################################################
    searchWorkflows: builder.infiniteQuery<
      Array<WorkflowDTOType>,
      { filters?: Array<FilterDTOType> },
      { page: number; size: number; filters?: Array<FilterDTOType> }
    >({
      infiniteQueryOptions: {
        initialPageParam: { page: 1, size: 10, filters: [] },
        getNextPageParam: (lastPage, allPages, lastPageParam) => {
          if (!lastPage || lastPage.length < lastPageParam.size) {
            return undefined;
          }
          return {
            page: lastPageParam.page + 1,
            size: lastPageParam.size,
            filters: lastPageParam.filters,
          };
        },
      },
      query: ({ pageParam: { page, size, filters = [] }, queryArg }) => ({
        url: `/catalog/workflow/search?page=${page}&size=${size}`,
        method: 'POST',
        body: {
          filters: queryArg?.filters ?? filters,
        },
      }),
      providesTags: [],
      keepUnusedDataFor: 0,
    }),
    // * ################################################################
    getWorkflowById: builder.query<WorkflowDTOType, { id: number }>({
      query: ({ id }) => ({
        url: `/catalog/workflow/${id}`,
        method: 'GET',
      }),
      keepUnusedDataFor: 0,
    }),
    // * ################################################################
    pauseWorkflow: builder.mutation<WorkflowDTOType, { id: number }>({
      query: ({ id }) => ({
        url: `/catalog/workflow/${id}/pause`,
        method: 'PUT',
      }),
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        await updateWorkflowInInfiniteQuery(id, dispatch);
      },
    }),
    // * ################################################################
    resumeWorkflow: builder.mutation<WorkflowDTOType, { id: number }>({
      query: ({ id }) => ({
        url: `/catalog/workflow/${id}/resume`,
        method: 'PUT',
      }),
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        await updateWorkflowInInfiniteQuery(id, dispatch);
      },
    }),
    // * ################################################################
    stopWorkflow: builder.mutation<WorkflowDTOType, { id: number }>({
      query: ({ id }) => ({
        url: `/catalog/workflow/${id}/stop`,
        method: 'PUT',
      }),
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        await updateWorkflowInInfiniteQuery(id, dispatch);
      },
    }),
    // * ################################################################
    rerunWorkflow: builder.mutation<WorkflowDTOType, { id: number }>({
      query: ({ id }) => ({
        url: `/catalog/workflow/${id}/rerun`,
        method: 'PUT',
      }),
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        await updateWorkflowInInfiniteQuery(id, dispatch);
      },
    }),
    // * ################################################################
    getQCPendingItems: builder.infiniteQuery<
      Array<WorkflowItemDTOType>,
      { workflowId: number; runId: number },
      { page: number; size: number }
    >({
      infiniteQueryOptions: {
        initialPageParam: { page: 1, size: 10 },
        getNextPageParam: (lastPage, allPages, lastPageParam) => {
          if (!lastPage || lastPage.length < lastPageParam.size) {
            return undefined;
          }
          return {
            page: lastPageParam.page + 1,
            size: lastPageParam.size,
          };
        },
      },
      query: ({ queryArg: { workflowId, runId }, pageParam }) => ({
        url: `/catalog/workflow/${workflowId}/run/${runId}/items/search?page=${pageParam.page}&size=${pageParam.size}`,
        method: 'POST',
        body: {
          filters: [
            {
              filterType: 'ISNOTEMPTY',
              field: 'proposedTags',
              value: '',
            },
          ],
        },
      }),
      providesTags: ['QCPendingItems'],
    }),
    // * ################################################################
    getQCProcessingItems: builder.infiniteQuery<
      Array<WorkflowItemDTOType>,
      { workflowId: number; runId: number },
      { page: number; size: number }
    >({
      infiniteQueryOptions: {
        initialPageParam: { page: 1, size: 10 },
        getNextPageParam: (lastPage, allPages, lastPageParam) => {
          if (!lastPage || lastPage.length < lastPageParam.size) {
            return undefined;
          }
          return {
            page: lastPageParam.page + 1,
            size: lastPageParam.size,
          };
        },
      },
      query: ({ queryArg: { workflowId, runId }, pageParam }) => ({
        url: `/catalog/workflow/${workflowId}/run/${runId}/items/search?page=${pageParam.page}&size=${pageParam.size}`,
        method: 'POST',
        body: {
          filters: [
            {
              filterType: 'IN',
              field: 'currentState',
              value: 'CREATED,PROCESSING',
            },
          ],
        },
      }),
      providesTags: ['QCProcessingItems'],
    }),
    // * ################################################################
    getQCPassedItems: builder.infiniteQuery<
      Array<WorkflowItemDTOType>,
      { workflowId: number; runId: number },
      { page: number; size: number }
    >({
      infiniteQueryOptions: {
        initialPageParam: { page: 1, size: 10 },
        getNextPageParam: (lastPage, allPages, lastPageParam) => {
          if (!lastPage || lastPage.length < lastPageParam.size) {
            return undefined;
          }
          return {
            page: lastPageParam.page + 1,
            size: lastPageParam.size,
          };
        },
      },
      query: ({ queryArg: { workflowId, runId }, pageParam }) => ({
        url: `/catalog/workflow/${workflowId}/run/${runId}/items/search?page=${pageParam.page}&size=${pageParam.size}`,
        method: 'POST',
        body: {
          filters: [
            {
              filterType: 'ISNOTEMPTY',
              field: 'acceptedTags',
              value: '',
            },
          ],
        },
      }),
      providesTags: ['QCPassedItems'],
    }),
    // * ################################################################
    getQCFailedItems: builder.infiniteQuery<
      Array<WorkflowItemDTOType>,
      { workflowId: number; runId: number },
      { page: number; size: number }
    >({
      infiniteQueryOptions: {
        initialPageParam: { page: 1, size: 10 },
        getNextPageParam: (lastPage, allPages, lastPageParam) => {
          if (!lastPage || lastPage.length < lastPageParam.size) {
            return undefined;
          }
          return {
            page: lastPageParam.page + 1,
            size: lastPageParam.size,
          };
        },
      },
      query: ({ queryArg: { workflowId, runId }, pageParam }) => ({
        url: `/catalog/workflow/${workflowId}/run/${runId}/items/search?page=${pageParam.page}&size=${pageParam.size}`,
        method: 'POST',
        body: {
          filters: [
            {
              filterType: 'ISNOTEMPTY',
              field: 'rejectedTags',
              value: '',
            },
          ],
        },
      }),
      providesTags: ['QCFailedItems'],
    }),
    // * ################################################################
    updateWorkflowItem: builder.mutation<
      WorkflowItemDTOType,
      {
        workflowId: number;
        runId: number;
        itemId: number;
        acceptedTags?: Array<string>;
        rejectedTags?: Array<string>;
        proposedTags: Array<string>;
      }
    >({
      query: ({
        workflowId,
        runId,
        itemId,
        acceptedTags = [],
        rejectedTags = [],
        proposedTags,
      }) => ({
        url: `/catalog/workflow/${workflowId}/run/${runId}/items/${itemId}`,
        method: 'PUT',
        body: {
          acceptedTags,
          rejectedTags,
          proposedTags,
        },
      }),

      invalidatesTags: [
        'QCPendingItems',
        'QCPassedItems',
        'QCFailedItems',
        'QCProcessingItems',
        'QCPendingItemsByTag',
      ],
    }),
    // * ################################################################
    getQCPendingItemsByTag: builder.infiniteQuery<
      Array<WorkflowItemDTOType>,
      { workflowId: number; runId: number; tags: Array<string> },
      { page: number; size: number }
    >({
      infiniteQueryOptions: {
        initialPageParam: { page: 1, size: 10 },
        getNextPageParam: (lastPage, allPages, lastPageParam) => {
          if (!lastPage || lastPage.length < lastPageParam.size) {
            return undefined;
          }
          return {
            page: lastPageParam.page + 1,
            size: lastPageParam.size,
          };
        },
      },
      query: ({ queryArg: { runId, workflowId, tags }, pageParam }) => ({
        url: `/catalog/workflow/${workflowId}/run/${runId}/items/search?page=${pageParam.page}&size=${pageParam.size}`,
        method: 'POST',
        body: {
          filters: [
            {
              filterType: 'IN',
              field: 'proposedTags',
              value: tags.join(','),
            },
          ],
        },
      }),
      providesTags: ['QCPendingItemsByTag'],
    }),
    // * ################################################################
    acceptWorkflowItem: builder.mutation<
      WorkflowItemDTOType,
      {
        workflowId: number;
        runId: number;
        itemId: number;
        acceptedTags: Array<string>;
      }
    >({
      query: ({ workflowId, runId, itemId, acceptedTags }) => ({
        url: `/catalog/workflow/${workflowId}/run/${runId}/items/${itemId}/accept`,
        method: 'PUT',
        body: {
          acceptedTags,
        },
      }),
      invalidatesTags: [
        'QCPendingItems',
        'QCPassedItems',
        'QCFailedItems',
        'QCProcessingItems',
        'QCPendingItemsByTag',
      ],
    }),
    // * ################################################################
    rejectWorkflowItem: builder.mutation<
      WorkflowItemDTOType,
      {
        workflowId: number;
        runId: number;
        itemId: number;
        rejectedTags: Array<string>;
      }
    >({
      query: ({ workflowId, runId, itemId, rejectedTags }) => ({
        url: `/catalog/workflow/${workflowId}/run/${runId}/items/${itemId}/reject`,
        method: 'PUT',
        body: {
          rejectedTags,
        },
      }),
      invalidatesTags: [
        'QCPendingItems',
        'QCPassedItems',
        'QCFailedItems',
        'QCProcessingItems',
        'QCPendingItemsByTag',
      ],
    }),
    // * ################################################################
  }),
});

export const {
  useCreateWorkflowMutation,
  useUpdateWorkflowMutation,
  useSearchWorkflowsInfiniteQuery,
  useGetWorkflowByIdQuery,
  usePauseWorkflowMutation,
  useResumeWorkflowMutation,
  useStopWorkflowMutation,
  useRerunWorkflowMutation,
  useGetQCPendingItemsInfiniteQuery,
  useGetQCProcessingItemsInfiniteQuery,
  useGetQCPassedItemsInfiniteQuery,
  useGetQCFailedItemsInfiniteQuery,
  useGetQCPendingItemsByTagInfiniteQuery,
  useUpdateWorkflowItemMutation,
  useAcceptWorkflowItemMutation,
  useRejectWorkflowItemMutation,
} = workflowApi;
